import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Plus,
  LogOut,
  Menu,
  X,
  Palette,
  Package,
  Scale,
  Settings,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Toaster } from "react-hot-toast";
import { api } from "@/services/api";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/organizations", label: "Organizations", icon: Building2, testid: "nav-organizations" },
  { to: "/app/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/app/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/app/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/app/balance-sheet", label: "Balance Sheet", icon: Scale, testid: "nav-balance-sheet" },
  { to: "/app/templates", label: "Templates", icon: Palette, testid: "nav-templates" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const doLogout = () => {
    logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0B] text-slate-900 dark:text-slate-100 flex">
      <Toaster position="top-right" />
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 h-screen z-40 w-72 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0A0A0B] transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
          <Link to="/app" data-testid="sidebar-brand">
            <Logo size="normal" />
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 mt-4">
          <button
            data-testid="new-invoice-cta"
            onClick={() => {
              setOpen(false);
              nav("/app/invoices/new");
            }}
            className="w-full flex items-center justify-center gap-2 bg-[#002FA7] text-white py-3 rounded-sm hover:bg-[#001F73] transition-colors font-semibold"
          >
            <Plus className="w-4 h-4" /> New Invoice
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Signed in</div>
              <div className="text-sm truncate" data-testid="user-email">
                {user?.email || "—"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => nav("/app/settings")}
                className="p-2 border border-slate-300 dark:border-slate-700 hover:border-slate-900 dark:hover:border-white rounded-sm"
                aria-label="Settings"
                title="Settings"
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                data-testid="logout-button"
                onClick={doLogout}
                className="p-2 border border-slate-300 dark:border-slate-700 hover:border-red-600 hover:text-red-600 rounded-sm"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0A0A0B]">
          <button onClick={() => setOpen(true)} data-testid="menu-open-button">
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/app">
            <Logo size="small" />
          </Link>
        </header>
        <motion.main
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="p-6 sm:p-10 max-w-[1400px] mx-auto"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
